import { Descriptions, Flex, Form, InputNumber, Modal, Table, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  clearPreallocationBreakdown,
  selectPaymentsSlice,
  setPreallocationBreakdown,
} from "features/payments/paymentsSlice";
import { useUserType } from "features/auth";
import { useParams } from "react-router-dom";
import { useMemo } from "react";
import { formatCurrency } from "common/utils";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useFetchCreditorPortalDebtorAccountsQuery } from "features/debtorAccountsTable/creditorPortal/debtorAccountsAPI";
import currency from "currency.js";

// Note: used in paymentConfiguration.jsx and setupPaymentSchedule.jsx of payments module in debtor profile
function PaymentAllocationItem({ accountId, category, originalValue }) {
  return (
    <Flex vertical gap={4}>
      <Form.Item
        name={[accountId, category]}
        normalize={(value) => (value === null || value === "" || value === undefined ? 0 : value)}
        noStyle
      >
        <InputNumber
          prefix="$"
          precision={2}
          min={0}
          max={originalValue}
          disabled={Number(originalValue) === 0}
        />
      </Form.Item>
      <Typography.Text type="secondary">{formatCurrency(originalValue)}</Typography.Text>
    </Flex>
  );
}

export default function PreallocationBreakdownModal({ title, onOk, onCancel }) {
  const { debtorId } = useParams();
  const { isAgencyUserType, isCreditorUserType } = useUserType();
  const paymentsSlice = useSelector(selectPaymentsSlice);
  const dispatch = useDispatch();

  const { data: agencyAccounts } = useFetchDebtorAccountsQuery(
    { debtorId },
    { skip: !isAgencyUserType }, // skip unless we are an agency user
  );

  const { data: creditorAccounts } = useFetchCreditorPortalDebtorAccountsQuery(
    { debtorId },
    { skip: !isCreditorUserType }, // skip if we are an agency user
  );

  const [form] = Form.useForm();
  const allValues = Form.useWatch([], form);

  const relevantAccounts = useMemo(
    () =>
      (isAgencyUserType ? agencyAccounts : creditorAccounts).filter((acct) =>
        paymentsSlice.selectedAccounts.includes(acct.id),
      ),
    [isAgencyUserType, agencyAccounts, creditorAccounts, paymentsSlice.selectedAccounts],
  );

  const allocatedAmount = useMemo(() => {
    if (!allValues) {
      return 0;
    }

    const total = Object.values(allValues)
      .flatMap((account) => Object.values(account))
      .reduce((sum, val) => sum + (val || 0), 0);

    return currency(total, { precision: 2 }).value;
  }, [allValues]);

  const totalAmount = useMemo(() => {
    return currency(paymentsSlice.paymentsSchedule[0].totalAmount, {
      precision: 2,
    }).value;
  }, [paymentsSlice.paymentsSchedule]);

  const onSubmit = async () => {
    if (totalAmount !== allocatedAmount) {
      return Modal.error({
        title: "Error",
        content: "The allocated amount must equal the total payment amount.",
        okText: "OK",
      });
    }

    dispatch(setPreallocationBreakdown(allValues));

    await onOk();
  };

  const onModalCancel = async () => {
    dispatch(clearPreallocationBreakdown());
    await onCancel();
  };

  return (
    <Modal
      okText="Confirm"
      cancelText={paymentsSlice.preallocationBreakdown ? "Reset" : "Cancel"}
      cancelButtonProps={{ danger: !!paymentsSlice.preallocationBreakdown }}
      maskClosable={false}
      closable={!paymentsSlice.preallocationBreakdown}
      title={title}
      open
      onOk={onSubmit}
      onCancel={onModalCancel}
      width="fit-content"
    >
      <Flex vertical gap={16}>
        <Form form={form} initialValues={paymentsSlice.preallocationBreakdown}>
          <Table
            scroll={{ x: "max-content" }}
            pagination={false}
            dataSource={relevantAccounts}
            columns={[
              {
                title: "Account",
                dataIndex: "externalId",
                key: "externalId",
                fixed: "left",
                width: 200,
                render: (text, record) => (
                  <Flex vertical gap={4}>
                    <Typography.Text>Account #{record.externalId}</Typography.Text>
                    <Typography.Text>{formatCurrency(record.totalBalance)}</Typography.Text>
                  </Flex>
                ),
              },
              {
                title: "Principal",
                render: (text, record) => (
                  <PaymentAllocationItem
                    category="principal"
                    accountId={record.id}
                    originalValue={record.principalBalance}
                  />
                ),
              },
              {
                title: "Agency Interest",
                render: (text, record) => (
                  <PaymentAllocationItem
                    category="interest"
                    accountId={record.id}
                    originalValue={record.interestBalance}
                  />
                ),
              },
              {
                title: "Client Interest",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="clientInterest"
                    accountId={record.id}
                    originalValue={record.clientInterestBalance}
                  />
                ),
              },
              {
                title: "Prejudgment Interest",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="prejudgmentInterest"
                    accountId={record.id}
                    originalValue={record.prejudgmentInterestBalance}
                  />
                ),
              },
              {
                title: "Attorney Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="attorneyFees"
                    accountId={record.id}
                    originalValue={record.attorneyFeesBalance}
                  />
                ),
              },
              {
                title: "Check Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="checkFees"
                    accountId={record.id}
                    originalValue={record.checkFeesBalance}
                  />
                ),
              },
              {
                title: "Collection Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="collectionFees"
                    accountId={record.id}
                    originalValue={record.collectionFeesBalance}
                  />
                ),
              },
              {
                title: "Court Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="courtFees"
                    accountId={record.id}
                    originalValue={record.courtFeesBalance}
                  />
                ),
              },
              {
                title: "Handling Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="handlingFees"
                    accountId={record.id}
                    originalValue={record.handlingFeesBalance}
                  />
                ),
              },
              {
                title: "Legal Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="legalFees"
                    accountId={record.id}
                    originalValue={record.legalFeesBalance}
                  />
                ),
              },
              {
                title: "Lien Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="lienFees"
                    accountId={record.id}
                    originalValue={record.lienFeesBalance}
                  />
                ),
              },
              {
                title: "Misc Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="miscFees"
                    accountId={record.id}
                    originalValue={record.miscFeesBalance}
                  />
                ),
              },
              {
                title: "Service Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="serviceFees"
                    accountId={record.id}
                    originalValue={record.serviceFeesBalance}
                  />
                ),
              },
              {
                title: "Client Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="clientFees"
                    accountId={record.id}
                    originalValue={record.clientFeesBalance}
                  />
                ),
              },
              {
                title: "Garnishment Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="garnishmentFees"
                    accountId={record.id}
                    originalValue={record.garnishmentFeesBalance}
                  />
                ),
              },
              {
                title: "Postjudgment Attorney Fees",
                render: (_, record) => (
                  <PaymentAllocationItem
                    category="postjudgmentAttorneyFees"
                    accountId={record.id}
                    originalValue={record.postjudgmentAttorneyFeesBalance}
                  />
                ),
              },
            ]}
          />
        </Form>
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Payment Amount">
            {formatCurrency(totalAmount)}
          </Descriptions.Item>
          <Descriptions.Item label="Allocated Amount">
            {formatCurrency(allocatedAmount)}
          </Descriptions.Item>
          <Descriptions.Item
            label="Remaining Amount"
            contentStyle={{
              color: allocatedAmount > totalAmount ? "red" : "inherit",
            }}
          >
            {formatCurrency(totalAmount - allocatedAmount)}
          </Descriptions.Item>
        </Descriptions>
      </Flex>
    </Modal>
  );
}
